import _ from "lodash";
import jsSVG from "jsSVG";
import naturalSortFn from "common/filters/natural_sort.pojo";

export default function svgService($document) {
    "ngInject";
    // initial pointer is set to this position
    //
    var pointer = {
        left: 0,
        top: 0,
    };

    // defines fixed pointer movements to achieve current layout
    //
    var POINTER_MOVEMENTS = {
        down: {
            cells: 75,
            columnHeaders: 90,
        },
        right: {
            cells: 70,
            content: 150,
            columnHeaders: 150,
        },
    };

    // config fonts and colors
    //
    var SVG_STYLE = {
        normal: {
            colors: {
                fill: "#FFF",
                stroke: "#999",
                "stroke-width": 1,
            },
            font: {
                size: 14,
            },
        },
        bold: {
            colors: {
                fill: "#FFF",
                stroke: "#000",
                "stroke-width": 1,
            },
            font: {
                size: 18,
                anchor: "middle",
                weight: "bold",
            },
        },
    };

    var DIMENSIONS = {
        offsetRight: 10,
        row: {
            heigth: 55,
        },
        structureCells: {
            width: 100,
        },
        deliverableCells: {
            width: 60,
        },
    };

    const PATTERN_FACTORS = {
        // precision when rounding any float values for patterns sizes/width/..
        PATTERN_PRECISION: 2,

        // Determines which shapes influence the global pattern size.
        // 0.0 = smallest shape
        // 0.2 = ignore the smallest few (20%) outliers
        // 0.5 = median
        PATTERN_SIZE_PERCENTILE: 0.2,

        // minimum number of times a pattern should repeat on the long side of a shape
        PATTERN_MIN_REPETITION: 2,

        // size of the pattern patch compared to the pattern size base value
        PATCH_SIZE_FACTOR: 1,

        // width of the wfc line compared to patch size
        WAITING_FOR_CONFIRMATION_LINE_WIDTH_FACTOR: 1 / 3,

        // width of the rejected lines compared to patch size
        REJECTED_LINE_WIDTH_FACTOR: 1 / 5,

        // radius of the out-of-sequence dots compared to patch size
        OUT_OF_SEQUENCE_DOT_RADIUS_FACTOR: 1 / 4,
    };

    // stores any patterns created for hatched styling
    //
    var svgPatterns = {
        hatched: [],
        crossHatched: [],
        dotted: [],
    };

    var service = {
        getSvg: getSvg,
        createSvg: createSvg,
        createPatternOnSvg: createPatternsOnSvg,
        findPatternBy: findPatternBy,
        computePatternSize: computePatternSize,
        resetSvgPatterns: resetSvgPatterns,
    };

    return service;

    function createDrawing(width, height) {
        var element = $document[0].createElement("div");

        // creates the SVG document
        //
        var draw = jsSVG(element);
        draw.viewbox({
            x: 0,
            y: 0,
            width: width || 5000,
            height: height || 5000,
        });
        return draw;
    }

    function drawStructureColumnHeader(draw, text, config) {
        return drawRectangleWithText(draw, text, pointer, config);
    }

    function drawDeliverablesColumnHeader(draw, text, config) {
        return drawRectangleWithText(draw, text, pointer, config);
    }

    function drawStructureCell(draw, text, config) {
        return drawRectangleWithText(draw, text, pointer, config);
    }

    function drawDeliverableCell(draw, text, config) {
        return drawRectangleWithText(draw, text, pointer, config, true);
    }

    function getSvg(svgContainerId) {
        var svg = $document[0].getElementById(svgContainerId).firstElementChild;

        return jsSVG.adopt(svg);
    }

    // public
    //
    function createSvg(rows, translations) {
        resetPointer();

        var totalWidth = caluclateTotalWidth(
            pointer.left,
            POINTER_MOVEMENTS,
            getMaxCells(rows)
        );
        var totalHeight = caluclateTotalHeight(
            pointer.top,
            POINTER_MOVEMENTS,
            rows.length
        );

        var header = {
            structureColumn: {
                width: DIMENSIONS.structureCells.width,
                height: DIMENSIONS.row.heigth,
                font: SVG_STYLE.bold.font,
                colors: SVG_STYLE.bold.colors,
            },
            deliverablesColumn: {
                width:
                    totalWidth -
                    pointer.left -
                    POINTER_MOVEMENTS.right.columnHeaders -
                    DIMENSIONS.offsetRight,
                height: DIMENSIONS.row.heigth,
                font: SVG_STYLE.bold.font,
                colors: SVG_STYLE.bold.colors,
            },
        };

        var cells = {
            structure: {
                width: DIMENSIONS.structureCells.width,
                height: DIMENSIONS.row.heigth,
                font: SVG_STYLE.bold.font,
                colors: SVG_STYLE.bold.colors,
            },
            deliverable: {
                width: DIMENSIONS.deliverableCells.width,
                height: DIMENSIONS.row.heigth,
                font: SVG_STYLE.normal.font,
                colors: SVG_STYLE.normal.colors,
            },
        };

        var draw = createDrawing(totalWidth, totalHeight);

        drawStructureColumnHeader(
            draw,
            translations.structureColumnTitle,
            header.structureColumn
        );

        movePointerRight(POINTER_MOVEMENTS.right.columnHeaders);
        drawDeliverablesColumnHeader(
            draw,
            translations.deliverablesColumnTitle,
            header.deliverablesColumn
        );

        _.forEach(rows, function (row) {
            resetPointerToLeftStart();
            movePointerDown(POINTER_MOVEMENTS.down.cells);

            drawStructureCell(draw, row.code, cells.structure);

            movePointerRight(POINTER_MOVEMENTS.right.content);

            // for each cell node on the entry
            //
            _.forEach(
                row.cells.sort(function (c1, c2) {
                    return naturalSortFn(c1.infoToDisplay, c2.infoToDisplay);
                }),
                function (cell) {
                    var svgNode = drawDeliverableCell(
                        draw,
                        cell.infoToDisplay,
                        cells.deliverable
                    );

                    setCodeAsId(svgNode, cell.code);

                    // will place the next box beside the last
                    //
                    movePointerRight(POINTER_MOVEMENTS.right.cells);
                }
            );
        });

        // once complete - return the SVG element to the caller
        return draw.svg();
    }

    function resetSvgPatterns() {
        svgPatterns = {
            hatched: [],
            crossHatched: [],
            dotted: [],
        };
    }

    // private
    //

    function resetPointer() {
        pointer = {
            left: 5,
            top: 5,
        };
    }

    function resetPointerToLeftStart() {
        pointer.left = 5;
    }

    function movePointerRight(amount) {
        pointer.left += amount;
    }

    function movePointerDown(amount) {
        pointer.top += amount;
    }

    function drawRectangleWithText(draw, text, pointer, config, alignTextLeft) {
        var r1 = drawRectangle(
            draw,
            config.width,
            config.height,
            pointer.left, // x
            pointer.top, // y
            config.colors
        );

        // get the positioning needed to draw the text
        //
        var p1 = r1.bbox();
        var p1TextX = alignTextLeft ? p1.x + 3 : p1.cx;
        var p1TextY = p1.y + 17;

        drawText(draw, text, p1TextX, p1TextY, config.font);

        return r1;
    }

    function drawRectangle(draw, width, height, positionX, positionY, colors) {
        var rect = draw
            .rect(width, height)
            .move(positionX, positionY)
            .attr(colors);

        return rect;
    }

    function drawText(draw, text, moveX, moveY, font) {
        var t1 = draw
            .text(text || "")
            .move(moveX, moveY)
            .font(font);
    }

    function setCodeAsId(rect, code) {
        rect.node.id = code;
        return rect;
    }

    function getMaxCells(data) {
        return data.reduce(function (max, row) {
            return Math.max(max, row.cells.length || 0);
        }, 0);
    }

    function caluclateTotalWidth(offset, movements, maxNumOfCells) {
        if (maxNumOfCells < 3) {
            maxNumOfCells = 3;
        }
        return (
            offset +
            movements.right.content +
            maxNumOfCells * movements.right.cells
        );
    }

    function caluclateTotalHeight(offset, movements, numOfRows) {
        var headerHeight =
            movements.down.cells + movements.down.columnHeaders + offset;
        var contentHeight = numOfRows * movements.down.cells;

        return contentHeight + headerHeight;
    }

    function createPatternsOnSvg(svg, color, patternSize) {
        const pair = _createPatternsOnSvg(svg, color, patternSize);
        _storePatterns(color, pair);
        return pair;
    }

    function _createPatternsOnSvg(svg, color, patternSize) {
        return {
            hatched: _createHatchPattern(svg, color, patternSize),
            crossHatched: _createCrossHatchedPattern(svg, color, patternSize),
            dotted: _createDottedPattern(svg, color, patternSize),
        };
    }

    function _storePatterns(color, patterns) {
        svgPatterns.hatched.push({
            color: color,
            pattern: patterns.hatched,
        });

        svgPatterns.crossHatched.push({
            color: color,
            pattern: patterns.crossHatched,
        });

        svgPatterns.dotted.push({
            color: color,
            pattern: patterns.dotted,
        });
    }

    function _createHatchPattern(svg, color, patternSize = 20) {
        const PATCH_SIZE = patternSize * PATTERN_FACTORS.PATCH_SIZE_FACTOR;
        const lineWidth =
            PATCH_SIZE *
            PATTERN_FACTORS.WAITING_FOR_CONFIRMATION_LINE_WIDTH_FACTOR;
        const LINE_WIDTH = Number.parseFloat(
            lineWidth.toPrecision(PATTERN_FACTORS.PATTERN_PRECISION)
        );

        const half = PATCH_SIZE / 2;

        return svg.pattern(PATCH_SIZE, PATCH_SIZE, function (add) {
            add.line(-half, PATCH_SIZE, PATCH_SIZE, -half).stroke({
                color,
                width: LINE_WIDTH,
                linecap: "square",
            });

            add.line(half, PATCH_SIZE, PATCH_SIZE, half).stroke({
                color,
                width: LINE_WIDTH,
                linecap: "square",
            });
        });
    }

    function _createCrossHatchedPattern(svg, color, patternSize = 20) {
        const PATCH_SIZE = patternSize * PATTERN_FACTORS.PATCH_SIZE_FACTOR;
        const lineWidth =
            PATCH_SIZE * PATTERN_FACTORS.REJECTED_LINE_WIDTH_FACTOR;
        const LINE_WIDTH = Number.parseFloat(
            lineWidth.toPrecision(PATTERN_FACTORS.PATTERN_PRECISION)
        );

        return svg.pattern(PATCH_SIZE, PATCH_SIZE, function (add) {
            add.line(0, 0, PATCH_SIZE, PATCH_SIZE).stroke({
                color,
                width: LINE_WIDTH,
                linecap: "round",
            });

            add.line(0, PATCH_SIZE, PATCH_SIZE, 0).stroke({
                color,
                width: LINE_WIDTH,
                linecap: "round",
            });
        });
    }

    function _createDottedPattern(svg, color, patternSize = 20) {
        const PATCH_SIZE = patternSize * PATTERN_FACTORS.PATCH_SIZE_FACTOR;
        const dotRadius =
            PATCH_SIZE * PATTERN_FACTORS.OUT_OF_SEQUENCE_DOT_RADIUS_FACTOR;
        const DOT_RADIUS = Number.parseFloat(
            dotRadius.toPrecision(PATTERN_FACTORS.PATTERN_PRECISION)
        );

        const c1 = PATCH_SIZE * 0.25;
        const c2 = PATCH_SIZE * 0.75;

        return svg.pattern(PATCH_SIZE, PATCH_SIZE, function (add) {
            add.circle(DOT_RADIUS * 2)
                .center(c1, c1)
                .fill(color);
            add.circle(DOT_RADIUS * 2)
                .center(c2, c2)
                .fill(color);
        });
    }

    function findPatternBy(type, color) {
        var exists = _.find(svgPatterns[type], function (pattern) {
            return pattern.color === color;
        });

        return exists && exists.pattern;
    }

    /**
     * Returns the p-th percentile of the given values.
     *
     * @param {number[]} values Sorted or unsorted numeric values.
     * @param {number} percentile Value between 0 (minimum) and 1 (maximum).
     * @returns {number|undefined}
     */
    function percentile(values, percentile) {
        if (!values.length) {
            return undefined;
        }

        const sorted = [...values].sort((a, b) => a - b);

        const index = (sorted.length - 1) * percentile;
        const lower = Math.floor(index);
        return sorted[lower];
    }

    /**
     * Computes a pattern size that guarantees visibility on most patternable
     * shapes in the SVG.
     *
     * The size is chosen from the PATTERN_SIZE_PERCENTILE percentile of all
     * eligible shapes, ignoring only the smallest outliers. It ensures the
     * pattern appears at least PATTERN_MIN_REPETITION times on the longer side
     * and at least once on the shorter side of the selected shape's bounding box.
     */
    function computePatternSize(elements) {
        const maxPatternSizes = elements.reduce(function (acc, el) {
            try {
                const bbox = el.getBBox();

                const width = bbox.width;
                const height = bbox.height;

                if (width <= 1 || height <= 1) {
                    return acc;
                }

                const shortSide = Math.min(width, height);
                const longSide = Math.max(width, height);

                const maxSizeForThisShape = Math.min(
                    longSide / PATTERN_FACTORS.PATTERN_MIN_REPETITION,
                    shortSide
                );

                acc.push(maxSizeForThisShape);
            } catch {
                // getBBox not available, e.g. in test environments
            }

            return acc;
        }, []);

        const patternSize = percentile(
            maxPatternSizes,
            PATTERN_FACTORS.PATTERN_SIZE_PERCENTILE
        );

        if (!patternSize || patternSize < 0) {
            return 20; // our default
        }

        return Number.parseFloat(
            patternSize.toPrecision(PATTERN_FACTORS.PATTERN_PRECISION)
        );
    }
}
